/**
 * Theme selection, kept pure so the resolution rules are testable.
 *
 * The user picks a *preference*; the applied theme is that preference resolved
 * against what the operating system is doing. Storing the preference rather than
 * the resolved value is what makes "Match system" mean something: it keeps
 * following the OS instead of freezing at whatever time of day it was chosen.
 */

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

export interface ThemeOption {
  id: ThemePreference;
  label: string;
  hint: string;
}

export const THEME_OPTIONS: readonly ThemeOption[] = [
  { id: 'dark', label: 'Dark Theme', hint: 'Always dark' },
  { id: 'light', label: 'Light Theme', hint: 'Always light' },
  { id: 'system', label: 'Match System', hint: 'Follow the operating system' },
] as const;

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

/** `theme-color` for the browser chrome, so mobile UI matches the editor. */
export const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#14161a',
  light: '#eef0f4',
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'dark' || value === 'light' || value === 'system';
}
